import type { AdminCompanySelectDto } from '@@types/api.types';
import type { Column, DataTableFilterActions } from '@components/data-table-filter/core/types';
import { Input } from '@components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover';
import { cn } from '@lib/utils';
import { TagIcon, XIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type TagOption = {
  label: string;
  value: string;
};

type SearchInputWithTagSuggestionsProps = {
  actions: DataTableFilterActions;
  columns: Column<AdminCompanySelectDto>[];
  onChange: (value: string | null) => void;
  tagOptions: TagOption[];
  value: string | null;
};

export const SearchInputWithTagSuggestions: React.FC<SearchInputWithTagSuggestionsProps> = (props) => {
  const { actions, columns, onChange, tagOptions, value } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const normalizeSearchToken = useCallback((input: string) => input.toLowerCase().replace(/#/g, '').trim(), []);

  const normalizedQuery = useMemo(() => normalizeSearchToken(value ?? ''), [normalizeSearchToken, value]);

  // Filter tag options by label or value; # is ignored during matching.
  const filteredTags = useMemo(() => {
    if (!normalizedQuery) return [];
    return tagOptions.filter((tag) => {
      const normalizedLabel = normalizeSearchToken(tag.label);
      const normalizedValue = normalizeSearchToken(tag.value);
      return normalizedLabel.includes(normalizedQuery) || normalizedValue.includes(normalizedQuery);
    });
  }, [normalizeSearchToken, normalizedQuery, tagOptions]);

  // Reset selected index when filtered tags change
  const filteredTagsLength = filteredTags.length;
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger on length change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [filteredTagsLength]);

  // Handle tag selection
  const handleTagSelect = useCallback(
    (tagValue: string) => {
      const companyTagsColumn = columns.find((c) => c.id === 'companyTags');
      if (companyTagsColumn) {
        actions.addFilterValue(companyTagsColumn, [tagValue]);
      }
      onChange(null);
      setIsOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    },
    [actions, columns, onChange],
  );

  // Handle input change
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const cleanValue = newValue.trim() || null;
      const shouldOpen = normalizeSearchToken(newValue).length > 0;

      onChange(cleanValue);
      setIsOpen(shouldOpen);
      setSelectedIndex(-1);
    },
    [normalizeSearchToken, onChange],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const popoverIsOpen = isOpen && filteredTags.length > 0;

      if (event.key === 'Escape') {
        if (popoverIsOpen) {
          setIsOpen(false);
          setSelectedIndex(-1);
          event.preventDefault();
          event.stopPropagation();
        } else if (value) {
          onChange(null);
          setIsOpen(false);
          setSelectedIndex(-1);
          event.preventDefault();
        }
        return;
      }

      if (!popoverIsOpen) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev + 1;
          return next >= filteredTags.length ? 0 : next;
        });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? filteredTags.length - 1 : next;
        });
      } else if (event.key === 'Enter' && selectedIndex >= 0 && filteredTags[selectedIndex]) {
        event.preventDefault();
        handleTagSelect(filteredTags[selectedIndex].value);
      }
    },
    [filteredTags, handleTagSelect, isOpen, onChange, selectedIndex, value],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  }, [onChange]);

  const showClearButton = Boolean(value);

  return (
    <Popover
      onOpenChange={(open) => {
        if (!open) {
          setSelectedIndex(-1);
        }
        setIsOpen(open);
      }}
      open={isOpen && filteredTags.length > 0}
    >
      <div className="relative flex-1">
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            className="pr-8"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search by name, slug, or tags"
            value={value ?? ''}
          />
        </PopoverAnchor>
        {showClearButton && (
          <button
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-gray-100"
            onClick={handleClear}
            type="button"
          >
            <XIcon className="size-4 text-gray-400" />
          </button>
        )}
      </div>
      <PopoverContent
        align="start"
        className="w-72 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        sideOffset={4}
      >
        <div className="text-xs font-medium text-muted-foreground px-2 py-1.5">Access Controls</div>
        <div className="max-h-[300px] overflow-y-auto">
          {filteredTags.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No access controls found.</div>
          ) : (
            filteredTags.map((tag, index) => {
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={tag.value}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
                    isSelected && 'bg-accent text-accent-foreground',
                  )}
                  onClick={() => handleTagSelect(tag.value)}
                  type="button"
                >
                  <TagIcon className="size-4 text-gray-500" />
                  <span>{tag.label}</span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
